import React, { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { createPaymentRecord, fetchAllPlans, fetchPaystackKeys } from "../redux/slices/userSlice";

const planDescriptions = {
  BASIC: "Kickstart your broadcasts with Pluglr essentials and reliable hosting.",
  PRO: "Increase capacity, collaborate with teams, and unlock advanced controls.",
  PREMIUM: "Extend your streaming windows and boost audience engagement at scale.",
  ENTERPRISE: "White-glove onboarding, dedicated support, and workflows built for you.",
  NULL: "Experiment with Pluglr using our starter toolkit at no cost.",
  DEFAULT: "Host, monetise, and grow your community with tailored live experiences.",
};

const featureLabels = [
  { key: "hasHostedEventPages", label: "Hosted event pages" },
  { key: "hasHostedChannelPages", label: "Hosted channel pages" },
  { key: "hasLiveChat", label: "Interactive live chat" },
  { key: "hasBasicAnalytics", label: "Audience analytics" },
  { key: "hasPodcastHosting", label: "Podcast hosting" },
  { key: "hasMultiChannelSupport", label: "Multi-channel distribution" },
  { key: "hasCustomAdsFreePlayer", label: "Ads-free player" },
  { key: "hasPrioritySupport", label: "Priority support" },
  { key: "hasUnlimitedAudience", label: "Unlimited audience seats" },
];

const faqs = [
  {
    id: 1,
    question: "Can I upgrade or downgrade later?",
    answer:
      "Absolutely. You can switch plans at any time from your Pluglr billing settings. Changes activate on your next billing date.",
  },
  {
    id: 2,
    question: "Do I need a card to try Pluglr?",
    answer:
      "No card is required to explore our free tools. Payment details are only needed when you activate a paid plan.",
  },
  {
    id: 3,
    question: "What payment methods do you support?",
    answer:
      "We process payments securely through Paystack — supporting major cards, bank transfers, and supported regional wallets.",
  },
  {
    id: 4,
    question: "Is priority support included in every plan?",
    answer:
      "Priority response times are included on the Pro tier and above. Basic and Starter plans receive standard chat and email support.",
  },
];

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours && minutes) {
    return `${hours}h ${minutes}m`;
  }

  if (hours) {
    return `${hours} hour${hours > 1 ? "s" : ""}`;
  }

  if (minutes) {
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  }

  return `${seconds} seconds`;
};

const PriceRap = styled.section`
  padding: 96px 0 120px;
  background: linear-gradient(180deg, #f6f9ff 0%, #ffffff 60%);

  .container {
    width: min(1140px, 92%);
    margin: 0 auto;
  }

  .page-heading {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 18px;
    align-items: center;
  }

  .page-heading h1 {
    font-size: clamp(2.5rem, 2vw + 2rem, 3.5rem);
    font-weight: 700;
    letter-spacing: -1px;
    color: #071f4a;
  }

  .page-heading p {
    max-width: 560px;
    font-size: 1rem;
    line-height: 1.6;
    color: #33415c;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(22, 119, 255, 0.1);
    color: #1677ff;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .billing-toggle {
    margin: 40px auto 0;
    display: flex;
    justify-content: center;
    gap: 8px;
    background: #ffffff;
    padding: 6px;
    border-radius: 999px;
    width: fit-content;
    box-shadow: 0 16px 40px rgba(7, 31, 74, 0.08);
  }

  .billing-toggle button {
    border: none;
    border-radius: 999px;
    padding: 10px 28px;
    font-size: 0.95rem;
    font-weight: 600;
    color: #33415c;
    background: transparent;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .billing-toggle button.active {
    background: linear-gradient(135deg, #1677ff 0%, #00b4d8 100%);
    color: #ffffff;
    box-shadow: 0 12px 24px rgba(22, 119, 255, 0.24);
  }

  .status {
    text-align: center;
    margin: 48px 0;
    color: #33415c;
    font-size: 1rem;
  }

  .status--error {
    color: #d14343;
  }

  .plans-grid {
    margin-top: 48px;
    display: grid;
    gap: 28px;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }

  .plan-card {
    background: #ffffff;
    border-radius: 24px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    border: 1px solid rgba(13, 35, 58, 0.08);
    box-shadow: 0 18px 36px rgba(10, 38, 71, 0.06);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }

  .plan-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 24px 48px rgba(7, 31, 74, 0.12);
  }

  .plan-card--featured {
    border: 1.5px solid rgba(22, 119, 255, 0.4);
    box-shadow: 0 28px 60px rgba(22, 119, 255, 0.14);
    position: relative;
    overflow: hidden;
  }

  .plan-card--featured::after {
    content: "Popular";
    position: absolute;
    top: 18px;
    right: -38px;
    transform: rotate(45deg);
    background: linear-gradient(135deg, #1677ff 0%, #00b4d8 100%);
    color: #ffffff;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    padding: 6px 56px;
  }

  .plan-card__header {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .plan-card__badge {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(7, 31, 74, 0.08);
    color: #071f4a;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  .plan-card__title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #031633;
  }

  .plan-card__description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #4b5675;
    min-height: 48px;
  }

  .plan-card__price {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .plan-card__amount {
    font-size: 2rem;
    font-weight: 700;
    color: #071f4a;
  }

  .plan-card__cycle {
    font-size: 0.95rem;
    color: #58627d;
  }

  .plan-card__note {
    font-size: 0.82rem;
    color: #8490a6;
  }

  .plan-card__cta {
    width: 100%;
    border: none;
    border-radius: 12px;
    padding: 14px 18px;
    background: linear-gradient(135deg, #12d27d 0%, #00b894 100%);
    color: #ffffff;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .plan-card__cta:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 28px rgba(18, 210, 125, 0.24);
  }

  .plan-card__cta--outline {
    background: transparent;
    border: 1px solid rgba(7, 31, 74, 0.2);
    color: #071f4a;
  }

  .plan-card__features {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .plan-card__feature {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    color: #3c486b;
    font-size: 0.92rem;
  }

  .plan-card__feature span {
    line-height: 1.4;
  }

  .faq-section {
    margin-top: 96px;
    background: #ffffff;
    border-radius: 24px;
    padding: 40px;
    box-shadow: 0 24px 48px rgba(7, 31, 74, 0.08);
  }

  .faq-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #071f4a;
    margin-bottom: 12px;
  }

  .faq-intro {
    color: #4b5675;
    margin-bottom: 28px;
  }

  .faq-item + .faq-item {
    border-top: 1px solid rgba(7, 31, 74, 0.08);
  }

  .faq-button {
    width: 100%;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #071f4a;
    cursor: pointer;
  }

  .faq-content {
    font-size: 0.95rem;
    line-height: 1.7;
    color: #4b5675;
    padding-bottom: 22px;
  }

  @media (max-width: 900px) {
    .plan-card {
      padding: 28px;
    }

    .faq-section {
      padding: 32px;
    }
  }

  @media (max-width: 600px) {
    padding: 72px 0 96px;

    .plans-grid {
      grid-template-columns: 1fr;
    }

    .faq-section {
      padding: 24px;
    }
  }
`;

const PricingList = () => {
  const dispatch = useDispatch();
  const {
    userAllPlans,
    planloading,
    error,
    paystackKeys,
    paymentLoading,
    paymentSuccess,
    paymentError,
    userData,
  } = useSelector((state) => state.userData);

  const [billingCycle, setBillingCycle] = useState("yearly");
  const [openFaqId, setOpenFaqId] = useState(faqs[0].id);
  const [activePlanId, setActivePlanId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [isPaystackScriptReady, setIsPaystackScriptReady] = useState(
    () => typeof window !== "undefined" && !!window.PaystackPop
  );
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [lastPaymentSummary, setLastPaymentSummary] = useState(null);

  const hasCharge = useMemo(() => {
    if (!lastPaymentSummary) return false;
    const parsed = Number(lastPaymentSummary.amount);
    return Number.isFinite(parsed) && parsed > 0;
  }, [lastPaymentSummary]);

  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false);
    setLastPaymentSummary(null);
  }, []);

  useEffect(() => {
    dispatch(fetchAllPlans());
  }, [dispatch]);

  const storedUserDetails = useMemo(() => {
    try {
      if (typeof window === "undefined") return {};
      const raw = localStorage.getItem("pluglrUserDetails");
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      console.warn("Unable to parse stored user details", err);
      return {};
    }
  }, []);

  const userEmail = useMemo(() => {
    return (
      userData?.email ||
      userData?.data?.email ||
      storedUserDetails?.email ||
      null
    );
  }, [storedUserDetails, userData]);

  const userName = useMemo(() => {
    const firstName = userData?.firstName || userData?.data?.fname || storedUserDetails?.fname;
    const lastName = userData?.lastName || userData?.data?.lname || storedUserDetails?.lname;
    return [firstName, lastName].filter(Boolean).join(" ");
  }, [storedUserDetails, userData]);

  useEffect(() => {
    if (!paymentSuccess) return;

    const payload = paymentSuccess?.data ?? paymentSuccess;

    setActivePlanId(null);
    setCheckoutError(null);

    const amountValue =
      typeof payload?.amount === "number"
        ? payload.amount
        : Number(
            payload?.amount ??
            payload?.totalAmount ??
            payload?.total_amount ??
            payload?.amountPaid ??
            payload?.amount_paid
          );

    setLastPaymentSummary({
      planType: payload?.planType ?? payload?.plan_type ?? null,
      planLabel:
        payload?.planLabel ??
        payload?.plan_label ??
        payload?.planType ??
        payload?.plan_type ??
        null,
      amount: Number.isFinite(amountValue) ? amountValue : 0,
      monthlyPlan: Boolean(payload?.monthlyPlan ?? payload?.monthly_plan),
      reference: payload?.reference ?? payload?.trxref ?? payload?.trans ?? null,
    });

    setIsSuccessModalOpen(true);
  }, [paymentSuccess]);

  const plans = useMemo(() => {
    const rawPlans = Array.isArray(userAllPlans?.data)
      ? userAllPlans.data
      : Array.isArray(userAllPlans)
      ? userAllPlans
      : [];

    return rawPlans.map((plan) => {
      const derivedFeatures = featureLabels
        .filter(({ key }) => plan[key])
        .map(({ label }) => label);

      const planMetrics = [
        plan.numOfAllowedUploads
          ? `${plan.numOfAllowedUploads.toLocaleString()} uploads`
          : null,
        plan.maxAudienceCount
          ? `${plan.maxAudienceCount.toLocaleString()} audience seats`
          : null,
        plan.streamingDurationSeconds
          ? `${formatDuration(plan.streamingDurationSeconds)} per live session`
          : null,
      ].filter(Boolean);

      return {
        ...plan,
        label: plan.name === "NULL" ? "STARTER" : plan.name,
        description: planDescriptions[plan.name] || planDescriptions.DEFAULT,
        features: [...derivedFeatures, ...planMetrics],
      };
    });
  }, [userAllPlans]);

  const formatPrice = (plan) => {
    const amount = billingCycle === "monthly" ? plan.priceMonthly : plan.priceYearly;
    if (!amount) {
      return "Free";
    }
    return `₦${amount.toLocaleString("en-NG")}`;
  };

  const priceSuffix = billingCycle === "monthly" ? "/month" : "/year";

  const formatSecondaryNote = (plan) => {
    const alternate = billingCycle === "monthly" ? plan.priceYearly : plan.priceMonthly;
    if (!alternate) return null;

    if (billingCycle === "yearly") {
      const monthlyEquivalent = Math.round(plan.priceYearly / 12);
      return `Equals ₦${monthlyEquivalent.toLocaleString("en-NG")} per month`;
    }

    return `Billed yearly at ₦${plan.priceYearly.toLocaleString("en-NG")}`;
  };

  const getCtaLabel = (plan, isCurrentPlanLoading) => {
    if (isCurrentPlanLoading) {
      return "Processing…";
    }
    if (!plan.priceMonthly && !plan.priceYearly) return "Activate for free";
    return "Choose plan";
  };

  const handleFaqToggle = (id) => {
    setOpenFaqId((prev) => (prev === id ? null : id));
  };

  const resolvePaystackPublicKey = useMemo(() => {
    if (!paystackKeys) return null;

    const payload = paystackKeys?.data ?? paystackKeys;

    const directKey =
      payload?.publicKey ||
      payload?.public_key ||
      payload?.PublicKey ||
      payload?.Public_key;

    if (directKey) return directKey;

    const testKey =
      payload?.testPublicKey ||
      payload?.test_public_key ||
      payload?.testKey;
    const liveKey =
      payload?.livePublicKey ||
      payload?.live_public_key ||
      payload?.liveKey;

    const isProductionEnv =
      process.env.REACT_APP_ENV === "production" ||
      process.env.NODE_ENV === "production" ||
      (typeof window !== "undefined" &&
        /pluglr|vercel|netlify|render|fly/.test(window.location.hostname));

    if (isProductionEnv) {
      return liveKey || testKey || null;
    }

    return testKey || liveKey || null;
  }, [paystackKeys]);

  const generateUUID = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `pluglr-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  };

  const handleSelectPlan = (plan) => {
    setCheckoutError(null);

    const rawAmount = Number(
      billingCycle === "monthly" ? plan.priceMonthly || 0 : plan.priceYearly || 0
    );

    const isCurrentPlanLoading = activePlanId === plan.name && paymentLoading;
    if (isCurrentPlanLoading) {
      return;
    }

    if (!userEmail) {
      setCheckoutError("We could not find an email tied to your account. Please update your profile before subscribing.");
      return;
    }

    if (rawAmount <= 0) {
      const timestamp = Date.now().toString();
      dispatch(
        createPaymentRecord({
          reference: generateUUID(),
          trans: timestamp,
          status: "SUCCESS",
          message: "Plan activated without charge.",
          transaction: timestamp,
          trxref: generateUUID(),
          planType: plan.name,
          monthlyPlan: billingCycle === "monthly",
          amount: 0,
        })
      );
      return;
    }

    if (!resolvePaystackPublicKey) {
      setCheckoutError("Payment gateway keys are unavailable. Please try again in a moment.");
      dispatch(fetchPaystackKeys());
      return;
    }

    if (!isPaystackScriptReady || typeof window === "undefined" || !window.PaystackPop) {
      setCheckoutError("Payment gateway is still loading. Please wait a few seconds and try again.");
      return;
    }

    const amountInKobo = Math.round(rawAmount * 100);
    const reference = generateUUID();
    const timestamp = Date.now().toString();

    try {
      setActivePlanId(plan.name);

      const handler = window.PaystackPop.setup({
        key: resolvePaystackPublicKey,
        email: userEmail,
        amount: amountInKobo,
        ref: reference,
        metadata: {
          custom_fields: [
            {
              display_name: "Plan",
              variable_name: "plan",
              value: plan.name,
            },
            {
              display_name: "Billing cycle",
              variable_name: "billingCycle",
              value: billingCycle,
            },
          ],
          customer_name: userName,
        },
        onClose: () => {
          setActivePlanId(null);
        },
        callback: (response) => {
          const payload = {
            reference: response?.reference || reference,
            trans: timestamp,
            status: response?.status?.toUpperCase?.() || "SUCCESS",
            message: response?.message || "Payment succeeded!",
            transaction: response?.transaction || timestamp,
            trxref: response?.trxref || response?.reference || reference,
            planType: plan.name,
            monthlyPlan: billingCycle === "monthly",
            amount: rawAmount,
          };

          dispatch(createPaymentRecord(payload));
        },
      });

      handler.openIframe();
    } catch (err) {
      console.error("Failed to initialize Paystack", err);
      setCheckoutError("Unable to start checkout. Please refresh and try again.");
      setActivePlanId(null);
    }
  };

  const isLoading = planloading && !plans.length;
  const hasError = !!error;

  const showGlobalStatus = checkoutError || paymentError || null;

  return (
    <PriceRap>
      <div className="container">
        <div className="page-heading">
          <span className="eyebrow">Flexible plans for every broadcaster</span>
          <h2>Choose the Pluglr plan that meets your ambition</h2>
          <p>
            Stream, host, and engage your audience without limits. Toggle between monthly or annual billing to find the best value for your next big launch.
          </p>
        </div>

        <div className="billing-toggle">
          <button
            type="button"
            className={billingCycle === "monthly" ? "active" : ""}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly billing
          </button>
          <button
            type="button"
            className={billingCycle === "yearly" ? "active" : ""}
            onClick={() => setBillingCycle("yearly")}
          >
            Annual billing · Save more
          </button>
        </div>

        {isLoading && <p className="status">Loading available plans…</p>}
        {hasError && !isLoading && (
          <p className="status status--error">Unable to fetch plans: {String(error)}</p>
        )}
        {showGlobalStatus && !isLoading && (
          <p className="status status--error">{showGlobalStatus}</p>
        )}
        {paymentSuccess && !isLoading && !showGlobalStatus && (
          <p className="status">Payment recorded successfully.</p>
        )}

        {/* {isSuccessModalOpen && (
          <div className="modal-overlay" role="presentation" onClick={closeSuccessModal}>
            <div
              className="modal-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="payment-success-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" className="modal-card__close" onClick={closeSuccessModal} aria-label="Close payment success modal">
                <Icon icon="mdi:close" width="18" height="18" color="#4b5675" />
              </button>
              <div className="modal-card__icon">
                <Icon icon="solar:confetti-bold" width="32" height="32" color="#12d27d" />
              </div>
              <h3 id="payment-success-title" className="modal-card__title">
                Payment successful
              </h3>
              <p className="modal-card__copy">
                {hasCharge ? (
                  <>
                    We’ve locked in your <span className="modal-card__highlight">{lastPaymentSummary?.planLabel || lastPaymentSummary?.planType || "plan"}</span>
                    {" "}
                    {lastPaymentSummary?.monthlyPlan ? (
                      <span>
                        at ₦{Number(lastPaymentSummary?.amount || 0).toLocaleString("en-NG")} per month.
                      </span>
                    ) : (
                      <span>
                        at ₦{Number(lastPaymentSummary?.amount || 0).toLocaleString("en-NG")} per year.
                      </span>
                    )}
                  </>
                ) : (
                  <>You’ve successfully activated your free plan.</>
                )}
              </p>
              {lastPaymentSummary?.reference && (
                <p className="modal-card__reference">Reference: {lastPaymentSummary.reference}</p>
              )}
              <div className="modal-actions">
                <button type="button" className="confirm" onClick={closeSuccessModal}>
                  Continue
                </button>
                <button type="button" className="secondary" onClick={closeSuccessModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )} */}

        {!isLoading && !hasError && (
          <div className="plans-grid">
            {plans.map((plan) => {
              const priceLabel = formatPrice(plan);
              const secondaryNote = formatSecondaryNote(plan);
              const isFeatured = ["PRO", "PREMIUM"].includes(plan.name);
              const isCurrentPlanLoading = paymentLoading && activePlanId === plan.name;
              const ctaLabel = getCtaLabel(plan, isCurrentPlanLoading);

              return (
                <article
                  key={plan.name}
                  className={`plan-card ${isFeatured ? "plan-card--featured" : ""}`}
                >
                  <header className="plan-card__header">
                    <span className="plan-card__badge">{plan.label}</span>
                    <h3 className="plan-card__title">{plan.name}</h3>
                    <p className="plan-card__description">{plan.description}</p>
                  </header>

                  <div>
                    <div className="plan-card__price">
                      <span className="plan-card__amount">{priceLabel}</span>
                      {priceLabel !== "Free" && <span className="plan-card__cycle">{priceSuffix}</span>}
                    </div>
                    {secondaryNote && <p className="plan-card__note">{secondaryNote}</p>}
                  </div>

                  <button
                    type="button"
                    className={`plan-card__cta ${isCurrentPlanLoading ? "plan-card__cta--disabled" : ""}`}
                    style={isCurrentPlanLoading ? { opacity: 0.7, pointerEvents: "none" } : undefined}
                    onClick={() => handleSelectPlan(plan)}
                  >
                    {ctaLabel}
                  </button>

                  <ul className="plan-card__features">
                    {plan.features.length === 0 && (
                      <li className="plan-card__feature">
                        <Icon icon="fluent-mdl2:check-mark" color="#12d27d" width="18" height="18" />
                        <span>All Pluglr essentials</span>
                      </li>
                    )}
                    {plan.features.map((feature) => (
                      <li key={feature} className="plan-card__feature">
                        <Icon icon="fluent-mdl2:check-mark" color="#12d27d" width="18" height="18" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        )}

        {/* <section className="faq-section">
          <h2 className="faq-title">Frequently asked questions</h2>
          <p className="faq-intro">Everything you need to know before you go live with Pluglr.</p>

          {faqs.map((faq) => {
            const isOpen = openFaqId === faq.id;
            return (
              <div key={faq.id} className="faq-item">
                <button type="button" className="faq-button" onClick={() => handleFaqToggle(faq.id)}>
                  <span>{faq.question}</span>
                  <Icon
                    icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                    width="22"
                    height="22"
                    color="#071f4a"
                  />
                </button>
                {isOpen && <div className="faq-content">{faq.answer}</div>}
              </div>
            );
          })}
        </section> */}
      </div>
    </PriceRap>
  );
};

export default PricingList;